import { enPaymentMethod } from 'enums/enPaymentMethod';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { apiService } from 'shared/services';
import { IPaymentResult } from 'interfaces/IPaymentResult';
import { of } from 'rxjs/observable/of';
import { timer } from 'rxjs/observable/timer';
import { Subject } from 'rxjs/Subject';

interface IWaiter {
  type: enPaymentMethod;
  time: number;
  result: any;
}

class PaymentWaiter {
  private startTime: number = null;
  private config$ = new BehaviorSubject<IWaiter | undefined>(undefined);
  private waiter$ = new BehaviorSubject<{ waiting: boolean; status: string; expiresAt: number }>({ waiting: false, status: 'idle', expiresAt: 0 });

  private cancel$: Subject<boolean> = new Subject<boolean>();

  public waitPayment(type: enPaymentMethod, result: IPaymentResult) {
    result.timeout -= 10;

    this.config$.next({ type, time: result.timeout, result });

    const expiresAt = new Date().getTime() + result.timeout * 1000;
    this.waiter$.next({ waiting: true, status: 'running', expiresAt });

    this.startTime = Math.ceil(new Date().getTime() / 1000);

    let diff = 0;
    let canceled = false;

    return of(true)
      .switchMap(() => {
        const now = Math.ceil(new Date().getTime() / 1000);
        diff = now - this.startTime;

        if (diff >= result.timeout) {
          this.waiter$.next({ waiting: true, status: 'waiting', expiresAt });
        }

        const date = new Date().getTime();
        const url = result.checkUrl;
        return apiService.get(`${url}${url.indexOf('?') > -1 ? '&' : '?'}time=${date}`, undefined, undefined, { maxAttempts: 1 }).map(() => true);
      })
      .retryWhen((err) => {
        return err.switchMap((e) => {
          if (e.message === 'pix timeout') {
            throw err;
          }

          return timer(2000);
        });
      })
      .merge(
        this.cancel$.do(() => {
          canceled = true;
        })
      )
      .take(1)
      .do((paid) => {
        if (canceled) {
          this.waiter$.next({ waiting: true, status: 'canceled', expiresAt });
          return;
        }

        this.waiter$.next({ waiting: true, status: paid ? 'paid' : 'canceled', expiresAt });
      })
      .switchMap((data) => {
        if (canceled) {
          this.waiter$.next({ waiting: false, status: 'completed', expiresAt });
        }

        return timer(2000)
          .do(() => {
            this.waiter$.next({ waiting: false, status: 'completed', expiresAt });
          })
          .mapTo(data);
      })
      .catch(() => {
        this.waiter$.next({ waiting: false, status: 'timeout', expiresAt });
        return of(false);
      });
  }

  public cancel() {
    this.cancel$.next(false);
    this.cancel$.complete();
    this.cancel$ = new Subject<boolean>();
  }

  public changeWaiterStatusToRunning() {
    const currentWaiterValue = this.waiter$.value;
    const config = this.config$.value;

    this.startTime = Math.ceil(new Date().getTime() / 1000);
    const expiresAt = new Date().getTime() + config.result.timeout * 1000;
    this.waiter$.next({ ...currentWaiterValue, status: 'running', waiting: true, expiresAt });
  }

  public getWaiter() {
    return this.config$
      .combineLatest(this.waiter$)
      .map(([config, waiter]) => {
        if (!config) {
          return null;
        }

        return {
          waiting: waiter.waiting,
          status: waiter.status,
          expiresAt: waiter.expiresAt,
          maxTime: config.time,
          type: config.type,
          result: config.result
        };
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }
}

export const paymentWaiter = new PaymentWaiter();
